/**
 * arbi-dev Engine Editor — Weight Components dedicated edit route per
 * scope-doc §2 amended dedicated-route convention.
 *
 * Single editing surface for buy_score_weight_components full-row consolidation
 * per Decision 1 relocated to dedicated route altitude. Both Weight components
 * region card (substrate × calculation cell) AND Buy Score composition
 * sub-region (transformation × decision cluster) cross-link here.
 *
 * Route: /admin/engine-editor/weight-components
 *   - AdminGate at top-level per ADR 37 (inherits from parent <AdminGate>
 *     wrapper in App.tsx)
 *   - Dual-container architecture per α-amendment §2 layout (matches parent
 *     Engine Editor page)
 *   - No sidebar entry (accessed via cell card cross-link or direct URL)
 *
 * Day 2 P3 implementation per scope-doc §5.8 amended canonical per-field
 * affordance table:
 *   - 9 columns selected at supabase query: id, key, label, penalty, is_available,
 *     sort_order, applies_to_metric, tooltip_text, notes (id included for row
 *     update path at Day 2.5 commit logic per Sol direction at Day 2 P0)
 *   - 8 columns rendered to admin: key (read-only identifier) + 7 edit-affordance
 *     columns (is_available, applies_to_metric, penalty ownership-cascading;
 *     tooltip_text, label, sort_order, notes operational-isolated)
 *
 * Day 2.5 P1-P4 composition shells + F33 gating + tooltip preview:
 *   - Per-cell click → ownership-cascading panel (slide-in from right) for
 *     is_available + applies_to_metric + penalty per scope §5.5.1
 *   - Per-cell click → operational-isolated inline editor (in-cell) for
 *     tooltip_text + label + sort_order + notes per scope §5.5.2
 *   - F33 dynamic gating (Branches A/B/C/D) overlay on ownership-cascading
 *     panel for is_available × applies_to_metric pair per scope §6.1 + §6.3
 *   - tooltip_text inline editor consumes resolveTooltip for preview rendering
 *     (Engine Editor = consumer 3 per scope §7.4 → Template 18 promotion
 *     trigger fires)
 *   - One cell editable at a time across both modes (block-when-active per Sol
 *     direction — clicking another cell while edit active is no-op)
 *
 * Day 2 P4 aggregate F33 warning at table-header per scope-doc §6.4 amended
 * dual treatment — copy verbatim per Atlas Day 2.5 D1 (α) lock disk
 * implementation; same warning content as Weight components region card on
 * parent Engine Editor page.
 *
 * Atlas warning import-vs-mirror decision (Day 2.5 P3): option (a) per Sol
 * direction — mirror inline; defer shared extraction. BACKLOG candidate filed
 * at Day 2.5 close per rule-of-three (5 inline instances).
 */

import { useCallback, useEffect, useState, type JSX } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { supabase } from '../lib/supabase';
import EngineEditorOwnershipCascadingPanel, {
  type OwnershipCascadingField,
} from '../components/engine-editor/affordances/EngineEditorOwnershipCascadingPanel';
import EngineEditorOperationalIsolatedCell, {
  type OperationalIsolatedField,
} from '../components/engine-editor/affordances/EngineEditorOperationalIsolatedCell';
import type { BuyScoreWeightComponent } from '../../../../src/types';

// Row shape selected at supabase query (9 columns; id included for Day 2.5
// commit logic per Sol direction at Day 2 P0).
interface BswcRow {
  id: string;
  key: string;
  label: string;
  penalty: boolean;
  is_available: boolean;
  sort_order: number;
  applies_to_metric: string | null;
  tooltip_text: string | null;
  notes: string | null;
}

// Per-column metadata used to render the per-cell affordance composition.
// Ownership-cascading composition (panel) for is_available + applies_to_metric +
// penalty per scope §5.5.1 amended. Operational-isolated composition (inline
// cell editor) for tooltip_text + label + sort_order + notes per scope §5.5.2
// amended.
type EditableColumn =
  | 'is_available'
  | 'applies_to_metric'
  | 'penalty'
  | 'tooltip_text'
  | 'label'
  | 'sort_order'
  | 'notes';

interface ColumnDef {
  key: EditableColumn;
  label: string;
  composition: 'ownership-cascading' | 'operational-isolated';
}

const EDITABLE_COLUMNS: ColumnDef[] = [
  { key: 'is_available', label: 'Available', composition: 'ownership-cascading' },
  { key: 'applies_to_metric', label: 'Metric', composition: 'ownership-cascading' },
  { key: 'penalty', label: 'Penalty', composition: 'ownership-cascading' },
  { key: 'tooltip_text', label: 'Tooltip', composition: 'operational-isolated' },
  { key: 'label', label: 'Label', composition: 'operational-isolated' },
  { key: 'sort_order', label: 'Order', composition: 'operational-isolated' },
  { key: 'notes', label: 'Notes', composition: 'operational-isolated' },
];

function formatCellValue(row: BswcRow, column: EditableColumn): string {
  const value = row[column];
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

function getColumnDef(column: EditableColumn): ColumnDef {
  return EDITABLE_COLUMNS.find((c) => c.key === column)!;
}

export default function EngineEditorWeightComponentsPage(): JSX.Element {
  useDocumentTitle('Weight Components — Engine Editor');

  const [rows, setRows] = useState<BswcRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  // Active edit state — one cell editable at a time across both modes per Sol
  // direction (block-when-active; clicking another cell while edit active is
  // no-op).
  const [activeEdit, setActiveEdit] = useState<{
    rowId: string;
    column: EditableColumn;
  } | null>(null);

  const fetchRows = useCallback((): (() => void) => {
    let cancelled = false;
    setLoading(true);
    // Route-mount fetch per scope-doc §5.7 amended path clarification — mirrors
    // Atlas WeightComponentsInspect.tsx lines 51-66 precedent (direct supabase
    // query on mount; per-row data fetched once + held in route-altitude state).
    supabase
      .from('buy_score_weight_components')
      .select('id, key, label, penalty, is_available, sort_order, applies_to_metric, tooltip_text, notes')
      .order('sort_order', { ascending: true })
      .then((res) => {
        if (cancelled) return;
        if (res.error) {
          setError(true);
          setLoading(false);
          return;
        }
        setRows((res.data ?? []) as unknown as BswcRow[]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return fetchRows();
  }, [fetchRows]);

  // Aggregate F33 trap state — same derivation as Weight components region card
  // on parent Engine Editor page (substrate fetcher computes same shape).
  // F33TrapWarning shared-component extraction candidate filed at BACKLOG per
  // Sol direction option (a) at Day 2.5 P0 close — mirror inline at Day 2.5;
  // defer extraction.
  const f33TrapRows = rows.filter(
    (r) => r.is_available === true && r.applies_to_metric == null,
  );

  const handleCellClick = (rowId: string, column: EditableColumn): void => {
    // Block-when-active per Sol direction at Day 2.5 P0 close (Part 2): if
    // any edit is active, clicking another cell is a no-op. Admin must save
    // or cancel the current edit first.
    if (activeEdit !== null) return;
    setActiveEdit({ rowId, column });
  };

  const handleEditClose = (): void => {
    setActiveEdit(null);
  };

  const handleEditSaved = (): void => {
    setActiveEdit(null);
    fetchRows();
  };

  const activeRow = activeEdit ? rows.find((r) => r.id === activeEdit.rowId) : null;
  const activeComposition = activeEdit ? getColumnDef(activeEdit.column).composition : null;

  return (
    <div className="h-full overflow-auto">
      {/* ── Title section (max-w-5xl per α-amendment §2 layout inheritance) ─ */}
      <div className="max-w-5xl mx-auto p-5 space-y-4">
        <div>
          <h1 className="text-xl font-semibold text-ink-bright">Weight components</h1>
          <p className="text-xs text-ink-muted mt-1">
            Buy Score weight rows shaping the engine&apos;s ranking calculation across
            components. Edit affordances per column per substrate-shape per scope-doc
            §5.5 (ownership-cascading + operational-isolated composition).
          </p>
        </div>
      </div>

      {/* ── Content section (max-w-7xl per α-amendment §2 layout inheritance) ─ */}
      <div className="max-w-7xl mx-auto px-5 pb-5 space-y-3">
        {/* Aggregate F33 warning at table-header altitude per scope-doc §6.4 amended.
            Copy verbatim per Atlas Day 2.5 D1 (α) lock disk implementation.
            Atlas warning import-vs-mirror decision: option (a) per Sol direction —
            mirror inline; defer shared extraction. */}
        {!loading && !error && f33TrapRows.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-500">
              <strong>Configuration trap:</strong> {f33TrapRows.length}{' '}
              {f33TrapRows.length === 1 ? 'weight component' : 'weight components'} active
              but not mapped to a metric ({f33TrapRows.map((r) => r.key).join(', ')}).
              Engine silently zeros the contribution.
            </p>
          </div>
        )}

        {/* Per-row edit table */}
        {error ? (
          <p className="text-xs text-ink-muted italic">
            Weight components substrate currently unavailable.
          </p>
        ) : loading ? (
          <div className="space-y-1" aria-label="Loading weight components rows">
            <div className="h-10 bg-surface-4 rounded animate-pulse" />
            <div className="h-10 bg-surface-4 rounded animate-pulse" />
            <div className="h-10 bg-surface-4 rounded animate-pulse" />
          </div>
        ) : (
          <div className="border border-border bg-surface-1 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-surface-2 border-b border-border">
                {/* T3.9 ADR-39 pixel audit: column headers follow the root
                    convention (UI_STANDARDS §3 — text-2xs uppercase
                    tracking-wider text-ink-muted; numeric columns right-
                    aligned, header and cell). */}
                <tr>
                  <th className="text-left px-3 py-2 text-2xs uppercase tracking-wider text-ink-muted font-medium">
                    Key
                  </th>
                  {EDITABLE_COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className={`px-3 py-2 text-2xs uppercase tracking-wider text-ink-muted font-medium ${
                        col.key === 'sort_order' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-border-subtle last:border-b-0">
                    {/* key — read-only first column per scope-doc §5.8 amended
                        sub-shape 1a identifier altitude */}
                    <td className="px-3 py-2 text-ink-secondary font-mono">{row.key}</td>
                    {EDITABLE_COLUMNS.map((col) => {
                      const isActiveCell =
                        activeEdit?.rowId === row.id && activeEdit.column === col.key;
                      const isOperationalActiveInCell =
                        isActiveCell && col.composition === 'operational-isolated';
                      const blocked = activeEdit !== null && !isActiveCell;
                      const cellText = formatCellValue(row, col.key);
                      return (
                        <td
                          key={col.key}
                          onClick={() => !blocked && handleCellClick(row.id, col.key)}
                          className={`px-3 py-2 text-ink-secondary transition-colors ${
                            col.key === 'sort_order' ? 'text-right font-mono' : ''
                          } ${
                            blocked
                              ? 'opacity-40 cursor-not-allowed'
                              : 'cursor-pointer hover:bg-surface-2'
                          }`}
                          aria-disabled={blocked}
                        >
                          {isOperationalActiveInCell ? (
                            <EngineEditorOperationalIsolatedCell
                              rowId={row.id}
                              field={col.key as OperationalIsolatedField}
                              currentValue={row[col.key as OperationalIsolatedField]}
                              allRows={rows as unknown as BuyScoreWeightComponent[]}
                              rowKey={row.key}
                              onSaved={handleEditSaved}
                              onCancel={handleEditClose}
                            />
                          ) : cellText === '—' ? (
                            <span className="text-ink-muted">{cellText}</span>
                          ) : (
                            cellText
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ownership-cascading panel — slide-in from right; renders at page level
          when activeEdit is an ownership-cascading column. F33 dynamic gating
          (Branches A/B/C/D per §6.1 + §6.3) handled internally by the panel
          based on row + field + proposed value. */}
      {activeEdit && activeRow && activeComposition === 'ownership-cascading' && (
        <EngineEditorOwnershipCascadingPanel
          row={activeRow}
          field={activeEdit.column as OwnershipCascadingField}
          onSaved={handleEditSaved}
          onClose={handleEditClose}
        />
      )}
    </div>
  );
}
